import { useEffect, useState, useMemo } from "react";
import { GuideGrid } from "@/components/GuideGrid";
import { GuideFilterSidebar, type GuideFilterState } from "@/components/GuideFilterSidebar";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useSeo, generateBreadcrumbSchema } from "@/hooks/useSeo";
import type { Guide, GuideKategorie } from "@/data/guides";
import { guideKategorien } from "@/data/guides";
import { BookOpen } from "lucide-react";

interface GuidesListeProps {
  onNavigate: (path: string) => void;
  initialKategorie?: GuideKategorie | null;
  initialSuche?: string | null;
}

export function GuidesListe({ onNavigate, initialKategorie, initialSuche }: GuidesListeProps) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<GuideFilterState>({
    kategorie: initialKategorie || null,
    tags: [],
    sortBy: "datum",
    order: "desc",
  });

  // Build query string from filters
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.kategorie) params.set("kategorie", filters.kategorie);
    if (filters.tags.length > 0) params.set("tags", filters.tags.join(","));
    if (filters.sortBy) params.set("sortBy", filters.sortBy);
    if (filters.order) params.set("order", filters.order);
    return params.toString();
  }, [filters]);

  // Fetch guides when filters change
  useEffect(() => {
    setLoading(true);

    const url = initialSuche
      ? `/api/guides?suche=${encodeURIComponent(initialSuche)}`
      : `/api/guides${queryString ? `?${queryString}` : ""}`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        setGuides(data.data || data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [queryString, initialSuche]);

  // Update filter when initialKategorie changes
  useEffect(() => {
    if (initialKategorie) {
      setFilters(prev => ({ ...prev, kategorie: initialKategorie }));
    }
  }, [initialKategorie]);

  const kategorieName = filters.kategorie
    ? guideKategorien.find(k => k.id === filters.kategorie)?.name
    : null;

  const breadcrumbItems = kategorieName
    ? [{ label: "Ratgeber", href: "/ratgeber" }, { label: kategorieName }]
    : [{ label: "Ratgeber" }];

  const title = initialSuche
    ? `Suchergebnisse für "${initialSuche}"`
    : kategorieName
      ? kategorieName
      : "Alle Ratgeber";

  // SEO
  useSeo({
    title: initialSuche
      ? `${title} | Ratgeber | zelt.kaufen`
      : kategorieName
        ? `${kategorieName} - Camping Ratgeber & Tipps | zelt.kaufen`
        : "Camping Ratgeber & Guides - Tipps für Anfänger & Profis | zelt.kaufen",
    description: initialSuche
      ? `${guides.length} Ratgeber-Artikel für "${initialSuche}"`
      : kategorieName
        ? `${guides.length} Ratgeber zum Thema ${kategorieName}. Expertentipps, Anleitungen und Kaufberatung für Camping und Outdoor.`
        : `${guides.length} Camping-Ratgeber mit Expertenwissen. Von Kaufberatung über Pflegetipps bis zu Camping-Guides - alles für Ihr Outdoor-Abenteuer.`,
    keywords: kategorieName
      ? `${kategorieName}, Camping Ratgeber, Zelt Tipps, Outdoor Guide`
      : "Camping Ratgeber, Zelt Kaufberatung, Outdoor Tipps, Camping Guide, Zelten Anfänger",
    type: "website",
    schema: generateBreadcrumbSchema(
      kategorieName
        ? [
            { name: "Startseite", url: window.location.origin },
            { name: "Ratgeber", url: `${window.location.origin}/ratgeber` },
            { name: kategorieName, url: window.location.href },
          ]
        : [
            { name: "Startseite", url: window.location.origin },
            { name: "Ratgeber", url: window.location.href },
          ]
    ),
  });

  return (
    <Container className="py-8">
      <Breadcrumb items={breadcrumbItems} className="mb-4" />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="lg:sticky lg:top-24">
            <div className="hidden lg:block">
              <h2 className="font-semibold mb-6">Filter</h2>
              <GuideFilterSidebar filters={filters} onChange={setFilters} />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold">{title}</h1>
              <p className="text-muted-foreground mt-1">
                {loading ? "Lädt..." : `${guides.length} Ratgeber gefunden`}
              </p>
            </div>
            {/* Mobile Filter Button */}
            <div className="lg:hidden">
              <GuideFilterSidebar filters={filters} onChange={setFilters} mobile />
            </div>
          </div>

          <GuideGrid guides={guides} onNavigate={onNavigate} loading={loading} />
        </main>
      </div>
    </Container>
  );
}
